import { useEffect, useMemo, useState } from 'react';
import { PhoneField, SelectField, TextArea, TextField } from '../components/FormField';
import Section from '../components/Section';
import { formatPhoneInput } from '../utils/formatters';
import { scrollToFirstError } from '../utils/scrollToFirstError';
import {
  COUNTRY_OPTIONS,
  STATE_OPTIONS_BY_COUNTRY,
  countryHasStateList,
  countryRequiresState,
} from '../utils/addressOptions';

const PLANT_STATUSES = ['Pre-Launch', 'Launch', 'Production', 'Wind-Down', 'Closed'];
// Plant classification within the supply chain — OEM assembly plant vs. a
// tiered supplier facility.
const PLANT_TYPES = ['OEM', 'Tier 1', 'Tier 2', 'Tier 3'];

// Helpers for repeater rows. Keep the shapes in one place so empty-row detection
// during save is straightforward.
function emptyContact() {
  return { name: '', company: '', title: '', email: '', phone: '', officePhone: '' };
}
const EMPTY_PPE = {
  vestColor: '',
  safetyGlasses: '',
  safetyShoes: '',
  sleeves: '',
  gloves: '',
  scratchGuards: '',
};
const EMPTY_FACILITY = { name: '', address: '', notes: '' };

function emptyVehicle() {
  return {
    make: '',
    model: '',
    program: '',
    // Start of Production / End of Production — optional ISO dates
    // (YYYY-MM-DD). Stored as plain strings so blank values
    // round-trip cleanly without becoming "Invalid Date".
    sopDate: '',
    eopDate: '',
    status: '',
  };
}

function emptyClosure() {
  return { startDate: '', endDate: '', reason: '' };
}

const MAX_VEHICLES = 10;

/**
 * Plant create / edit form. Used by the Plants index (New plant) and will be
 * re-used by the Plant detail page (Edit). Long on purpose — one form,
 * sectioned with headings, so every field in the plant book is reachable
 * without tab-hopping.
 */
export default function PlantForm({
  initial,
  readOnly = false,
  saving = false,
  onSave,
  onCancel,
}) {
  const [model, setModel] = useState(() => buildModel(initial));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setModel(buildModel(initial));
    setErrors({});
  }, [initial?.id]);

  const set = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  // Country / State dropdown options. Keep any non-standard saved value as an
  // option so editing a legacy plant doesn't blank a free-typed country/state.
  const countryOptions = useMemo(() => {
    const opts = [...COUNTRY_OPTIONS];
    if (model.Country && !opts.includes(model.Country)) opts.unshift(model.Country);
    return opts;
  }, [model.Country]);
  const stateOptions = useMemo(() => {
    const base = STATE_OPTIONS_BY_COUNTRY[model.Country] || [];
    const opts = [...base];
    if (model.State && !opts.includes(model.State)) opts.unshift(model.State);
    return opts;
  }, [model.Country, model.State]);
  const setPpe = (k, v) =>
    setModel((m) => ({ ...m, ppe: { ...m.ppe, [k]: v } }));

  // Sequencing Facilities repeater helpers. Same shape as the other
  // array-of-card repeaters in this form (plantContacts, plantClosures).
  const setFacility = (i, patch) =>
    setModel((m) => {
      const next = [...(m.sequencingFacilities || [])];
      next[i] = { ...next[i], ...patch };
      return { ...m, sequencingFacilities: next };
    });
  const addFacility = () =>
    setModel((m) => ({
      ...m,
      sequencingFacilities: [
        ...(m.sequencingFacilities || []),
        { ...EMPTY_FACILITY },
      ],
    }));
  const removeFacility = (i) =>
    setModel((m) => ({
      ...m,
      sequencingFacilities: (m.sequencingFacilities || []).filter(
        (_, idx) => idx !== i,
      ),
    }));

  // Crew shift row helpers retired — see Crew Shifts section below.
  // The three day-of-week pickers + Shift Notes textarea use the
  // generic `set` helper directly. Legacy crewShifts data is still
  // round-tripped on save (see emit handler) so a re-save doesn't
  // wipe a plant's historical schedule rows.

  // Vehicle row helpers ---------------------------------------------------
  const setVehicle = (i, patch) =>
    setModel((m) => {
      const next = [...(m.vehicles || [])];
      next[i] = { ...next[i], ...patch };
      return { ...m, vehicles: next };
    });
  const addVehicle = () =>
    setModel((m) => {
      const current = m.vehicles || [];
      if (current.length >= MAX_VEHICLES) return m;
      return { ...m, vehicles: [...current, emptyVehicle()] };
    });
  const removeVehicle = (i) =>
    setModel((m) => ({
      ...m,
      vehicles: (m.vehicles || []).filter((_, idx) => idx !== i),
    }));

  // Plant closure row helpers --------------------------------------------
  const setClosure = (i, patch) =>
    setModel((m) => {
      const next = [...(m.plantClosures || [])];
      next[i] = { ...next[i], ...patch };
      return { ...m, plantClosures: next };
    });
  const addClosure = () =>
    setModel((m) => ({
      ...m,
      plantClosures: [...(m.plantClosures || []), emptyClosure()],
    }));
  const removeClosure = (i) =>
    setModel((m) => ({
      ...m,
      plantClosures: (m.plantClosures || []).filter((_, idx) => idx !== i),
    }));

  // Plant contact row helpers --------------------------------------------
  const setContact = (i, patch) =>
    setModel((m) => {
      const next = [...(m.plantContacts || [])];
      next[i] = { ...next[i], ...patch };
      return { ...m, plantContacts: next };
    });
  const addContact = () =>
    setModel((m) => ({
      ...m,
      plantContacts: [...(m.plantContacts || []), emptyContact()],
    }));
  const removeContact = (i) =>
    setModel((m) => ({
      ...m,
      plantContacts: (m.plantContacts || []).filter((_, idx) => idx !== i),
    }));
  // Reorder contacts within the OEM Plant Contacts list. `dir` is -1
  // (move up) or +1 (move down). Out-of-range moves are silent no-ops
  // so the up/down buttons can call this without bounds-checking. Any
  // change of order is persisted on save like the rest of the list.
  const moveContact = (i, dir) =>
    setModel((m) => {
      const list = [...(m.plantContacts || [])];
      const j = i + dir;
      if (j < 0 || j >= list.length) return m;
      [list[i], list[j]] = [list[j], list[i]];
      return { ...m, plantContacts: list };
    });

  // Validation ------------------------------------------------------------
  // Required sections: Basic Info (Name, Code, Plant Type) and Address.
  // Optional: OEM, Vehicles, Safety Equipment (PPE), Crew Shifts, Plant
  // Closures, Sequencing Facility, Notes, and OEM Plant Contacts.
  const validate = () => {
    const next = {};
    const req = (k, msg = 'Required') => {
      if (!model[k] || (typeof model[k] === 'string' && !model[k].trim())) {
        next[k] = msg;
      }
    };
    req('Title');
    req('PlantCode');
    // Basic: no spaces in the code, keep URL-friendly.
    if (model.PlantCode && /\s/.test(model.PlantCode)) {
      next.PlantCode = 'Plant code can’t contain spaces.';
    }
    req('PlantType');
    // Address
    req('AddressStreet');
    req('City');
    // State / Province is required only for the United States and Canada;
    // optional for every other country.
    if (countryRequiresState(model.Country)) req('State');
    req('PostalCode');
    req('Country');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!validate()) {
      scrollToFirstError();
      return;
    }

    // Strip blank rows from repeaters so placeholder rows don't persist.
    const crewShifts = (model.crewShifts || []).filter(
      (s) => s.shift?.trim() || s.schedule?.trim(),
    );
    const plantContacts = (model.plantContacts || []).filter(
      (c) => c.name || c.company || c.title || c.email || c.phone || c.officePhone,
    );
    const vehicles = (model.vehicles || [])
      .map((v) => ({
        make: (v.make || '').trim(),
        model: (v.model || '').trim(),
        program: (v.program || '').trim(),
        sopDate: (v.sopDate || '').trim(),
        eopDate: (v.eopDate || '').trim(),
        status: (v.status || '').trim(),
      }))
      .filter(
        (v) =>
          v.make ||
          v.model ||
          v.program ||
          v.sopDate ||
          v.eopDate ||
          v.status,
      );
    // Drop blank closure rows so placeholder entries don't persist; trim each
    // field so the detail page renders cleanly.
    const plantClosures = (model.plantClosures || [])
      .map((c) => ({
        startDate: (c.startDate || '').trim(),
        endDate: (c.endDate || '').trim(),
        reason: (c.reason || '').trim(),
      }))
      .filter((c) => c.startDate || c.endDate || c.reason);
    onSave({
      ...model,
      crewShifts,
      plantContacts,
      vehicles,
      plantClosures,
      PlantCode: model.PlantCode.trim().toUpperCase(),
    });
  };

  const inputs = !readOnly;

  return (
    <form onSubmit={submit} noValidate>
      <Section title="Basic Info">
        <div className="form-grid">
          <TextField
            label="Plant Name *"
            value={model.Title}
            onChange={(v) => set('Title', v)}
            error={errors.Title}
            disabled={!inputs}
            full
          />
          <TextField
            label="Plant Code *"
            value={model.PlantCode}
            onChange={(v) => set('PlantCode', v.toUpperCase())}
            error={errors.PlantCode}
            disabled={!inputs}
          />
          <TextField
            label="OEM"
            value={model.OEM}
            onChange={(v) => set('OEM', v)}
            error={errors.OEM}
            disabled={!inputs}
          />
          <SelectField
            label="Plant Type *"
            value={model.PlantType || ''}
            onChange={(v) => set('PlantType', v)}
            options={PLANT_TYPES}
            error={errors.PlantType}
            disabled={!inputs}
          />
        </div>
      </Section>

      <Section title="Vehicles">
        <Row
          onAdd={
            inputs && (model.vehicles || []).length < MAX_VEHICLES
              ? addVehicle
              : undefined
          }
          addLabel="+ Add Vehicle"
        >
          {errors.vehicles && (
            <p
              className="error"
              style={{ margin: '0 0 8px', fontSize: 13 }}
            >
              {errors.vehicles}
            </p>
          )}
          {(model.vehicles || []).length === 0 && (
            <p className="muted tiny">No vehicles yet.</p>
          )}
          {(model.vehicles || []).map((v, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                marginBottom: 10,
                paddingBottom: 10,
                borderBottom:
                  i === (model.vehicles || []).length - 1
                    ? 'none'
                    : '1px solid var(--brand-border)',
              }}
            >
              {/* Row 1 — identity. Status moved down to row 2
                  alongside the dates, so this row only carries
                  Make / Model / Program plus the remove button. */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr auto',
                  gap: 8,
                }}
              >
                <input
                  placeholder="Vehicle Make"
                  value={v.make || ''}
                  onChange={(e) => setVehicle(i, { make: e.target.value })}
                  disabled={!inputs}
                  style={{ padding: '6px 10px', border: '1px solid var(--brand-border)', borderRadius: 4 }}
                />
                <input
                  placeholder="Vehicle Model"
                  value={v.model || ''}
                  onChange={(e) => setVehicle(i, { model: e.target.value })}
                  disabled={!inputs}
                  style={{ padding: '6px 10px', border: '1px solid var(--brand-border)', borderRadius: 4 }}
                />
                <input
                  placeholder="Program"
                  value={v.program || ''}
                  onChange={(e) => setVehicle(i, { program: e.target.value })}
                  disabled={!inputs}
                  style={{ padding: '6px 10px', border: '1px solid var(--brand-border)', borderRadius: 4 }}
                />
                {inputs && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => removeVehicle(i)}
                  >
                    ×
                  </button>
                )}
              </div>
              {/* Row 2 — SOP / EOP dates and Status. Date inputs
                  and the status select all live in labeled
                  flex-columns so the row reads consistently. All
                  optional. */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: 8,
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    fontSize: 11,
                    color: 'var(--brand-muted)',
                  }}
                >
                  SOP Date
                  <input
                    type="date"
                    value={v.sopDate || ''}
                    onChange={(e) => setVehicle(i, { sopDate: e.target.value })}
                    disabled={!inputs}
                    style={{ padding: '6px 10px', border: '1px solid var(--brand-border)', borderRadius: 4 }}
                  />
                </label>
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    fontSize: 11,
                    color: 'var(--brand-muted)',
                  }}
                >
                  EOP Date
                  <input
                    type="date"
                    value={v.eopDate || ''}
                    onChange={(e) => setVehicle(i, { eopDate: e.target.value })}
                    disabled={!inputs}
                    style={{ padding: '6px 10px', border: '1px solid var(--brand-border)', borderRadius: 4 }}
                  />
                </label>
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    fontSize: 11,
                    color: 'var(--brand-muted)',
                  }}
                >
                  Status
                  <select
                    value={v.status || ''}
                    onChange={(e) => setVehicle(i, { status: e.target.value })}
                    disabled={!inputs}
                    style={{ padding: '6px 10px', border: '1px solid var(--brand-border)', borderRadius: 4 }}
                  >
                    <option value="">Status…</option>
                    {PLANT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          ))}
          {(model.vehicles || []).length >= MAX_VEHICLES && inputs && (
            <p className="muted tiny" style={{ marginTop: 4 }}>
              Maximum of {MAX_VEHICLES} vehicles per plant.
            </p>
          )}
        </Row>
        {/* Free-form notes covering the production schedule across
            this plant's vehicles — slip dates, holiday shutdowns,
            ramp plans, etc. Sits under the vehicles repeater so it
            reads as a footer to that section. */}
        <Row>
          <TextArea
            label="Schedule Notes"
            value={model.VehicleScheduleNotes || ''}
            onChange={(v) => set('VehicleScheduleNotes', v)}
            rows={3}
            disabled={!inputs}
          />
        </Row>
      </Section>

      <Section title="Address">
        <div className="form-grid">
          <TextField
            label="Street *"
            value={model.AddressStreet}
            onChange={(v) => set('AddressStreet', v)}
            error={errors.AddressStreet}
            disabled={!inputs}
            full
          />
          <TextField
            label="Address Line 2"
            value={model.AddressStreet2}
            onChange={(v) => set('AddressStreet2', v)}
            disabled={!inputs}
            full
          />
          <TextField
            label="Address Line 3"
            value={model.AddressStreet3}
            onChange={(v) => set('AddressStreet3', v)}
            disabled={!inputs}
            full
          />
          <SelectField
            label="Country *"
            value={model.Country}
            onChange={(v) => set('Country', v)}
            options={countryOptions}
            placeholder="— select country —"
            error={errors.Country}
            disabled={!inputs}
          />
          {countryHasStateList(model.Country) ? (
            <SelectField
              label={`State / Province${countryRequiresState(model.Country) ? ' *' : ''}`}
              value={model.State}
              onChange={(v) => set('State', v)}
              options={stateOptions}
              placeholder={
                model.Country ? '— select state/province —' : '— select country first —'
              }
              error={errors.State}
              disabled={!inputs}
            />
          ) : (
            <TextField
              label={`State / Province${countryRequiresState(model.Country) ? ' *' : ''}`}
              value={model.State}
              onChange={(v) => set('State', v)}
              error={errors.State}
              disabled={!inputs || !model.Country}
              placeholder={
                model.Country
                  ? 'Enter state / province / region'
                  : '— select country first —'
              }
            />
          )}
          <TextField
            label="City *"
            value={model.City}
            onChange={(v) => set('City', v)}
            error={errors.City}
            disabled={!inputs}
          />
          <TextField
            label="Zip / Postal Code *"
            value={model.PostalCode}
            onChange={(v) => set('PostalCode', v)}
            error={errors.PostalCode}
            disabled={!inputs}
          />
        </div>
      </Section>

      <Section title="Crew Shifts">
        {/* Day-of-week shift counts — weekend coverage often differs
            from weekday coverage (e.g., 3 weekday shifts, 1 Saturday
            shift, no Sunday production), so each day type gets its
            own picker. Stored as strings "0"–"4" to match the existing
            number-column conventions on the plants list. Empty string
            means "not specified" and renders as a dash on the detail
            page. */}
        <div className="form-grid" style={{ marginBottom: 12 }}>
          <SelectField
            label="Shifts on Weekdays"
            value={model.ShiftsWeekdays || ''}
            onChange={(v) => set('ShiftsWeekdays', v)}
            options={['0', '1', '2', '3', '4']}
            disabled={!inputs}
          />
          <SelectField
            label="Shifts on Saturdays"
            value={model.ShiftsSaturdays || ''}
            onChange={(v) => set('ShiftsSaturdays', v)}
            options={['0', '1', '2', '3', '4']}
            disabled={!inputs}
          />
          <SelectField
            label="Shifts on Sundays"
            value={model.ShiftsSundays || ''}
            onChange={(v) => set('ShiftsSundays', v)}
            options={['0', '1', '2', '3', '4']}
            disabled={!inputs}
          />
        </div>
        {/* Shift Notes — replaces the per-shift schedule table that
            used to live here. Free-form so a Director can record
            shift start/end times, OT rules, or which supplier covers
            which shift without forcing a rigid row structure. */}
        <TextArea
          label="Shift Notes"
          value={model.ShiftNotes || ''}
          onChange={(v) => set('ShiftNotes', v)}
          disabled={!inputs}
          full
        />
      </Section>

      <Section title="Safety Equipment (PPE)">
        <div className="form-grid">
          <TextField
            label="Vest Color"
            value={model.ppe.vestColor}
            onChange={(v) => setPpe('vestColor', v)}
            error={errors['ppe.vestColor']}
            disabled={!inputs}
          />
          <TextField
            label="Safety Glasses"
            value={model.ppe.safetyGlasses}
            onChange={(v) => setPpe('safetyGlasses', v)}
            error={errors['ppe.safetyGlasses']}
            disabled={!inputs}
          />
          <TextField
            label="Safety Shoes"
            value={model.ppe.safetyShoes}
            onChange={(v) => setPpe('safetyShoes', v)}
            error={errors['ppe.safetyShoes']}
            disabled={!inputs}
          />
          <TextField
            label="Sleeves"
            value={model.ppe.sleeves}
            onChange={(v) => setPpe('sleeves', v)}
            error={errors['ppe.sleeves']}
            disabled={!inputs}
          />
          <TextField
            label="Gloves"
            value={model.ppe.gloves}
            onChange={(v) => setPpe('gloves', v)}
            error={errors['ppe.gloves']}
            disabled={!inputs}
          />
          <TextField
            label="Scratch Guards / ISD Protection"
            value={model.ppe.scratchGuards}
            onChange={(v) => setPpe('scratchGuards', v)}
            error={errors['ppe.scratchGuards']}
            disabled={!inputs}
          />
        </div>
      </Section>

      <Section title="Plant Closures">
        <p className="muted tiny" style={{ marginTop: -6, marginBottom: 10 }}>
          These dates are used for billing and invoicing. Make sure they're
          accurate.
        </p>
        <Row
          onAdd={inputs ? addClosure : undefined}
          addLabel="+ Add Closure"
        >
          {(model.plantClosures || []).length === 0 && (
            <p className="muted tiny">No closures scheduled.</p>
          )}
          {(model.plantClosures || []).map((c, i) => (
            <div
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1.6fr auto',
                gap: 8,
                marginBottom: 6,
              }}
            >
              <input
                type="date"
                value={c.startDate || ''}
                onChange={(e) => setClosure(i, { startDate: e.target.value })}
                disabled={!inputs}
                style={{ padding: '6px 10px', border: '1px solid var(--brand-border)', borderRadius: 4 }}
              />
              <input
                type="date"
                value={c.endDate || ''}
                onChange={(e) => setClosure(i, { endDate: e.target.value })}
                disabled={!inputs}
                style={{ padding: '6px 10px', border: '1px solid var(--brand-border)', borderRadius: 4 }}
              />
              <input
                placeholder="Reason"
                value={c.reason || ''}
                onChange={(e) => setClosure(i, { reason: e.target.value })}
                disabled={!inputs}
                style={{ padding: '6px 10px', border: '1px solid var(--brand-border)', borderRadius: 4 }}
              />
              {inputs && (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => removeClosure(i)}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </Row>
      </Section>

      <Section title="OEM Plant Contacts">
        <Row
          onAdd={inputs ? addContact : undefined}
          addLabel="+ Add Contact"
        >
          {(model.plantContacts || []).length === 0 && (
            <p className="muted tiny">No contacts yet.</p>
          )}
          {(model.plantContacts || []).map((c, i) => (
            <div
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1.2fr 1fr 1fr auto',
                gap: 6,
                marginBottom: 6,
              }}
            >
              <input placeholder="Name" value={c.name} onChange={(e) => setContact(i, { name: e.target.value })} disabled={!inputs} />
              <input placeholder="Company" value={c.company || ''} onChange={(e) => setContact(i, { company: e.target.value })} disabled={!inputs} />
              <input placeholder="Title" value={c.title} onChange={(e) => setContact(i, { title: e.target.value })} disabled={!inputs} />
              <input placeholder="Email" value={c.email} onChange={(e) => setContact(i, { email: e.target.value })} disabled={!inputs} />
              <input type="tel" placeholder="Cell Phone" value={c.phone} onChange={(e) => setContact(i, { phone: formatPhoneInput(e.target.value) })} disabled={!inputs} />
              <input type="tel" placeholder="Office Phone (ext. supported)" value={c.officePhone || ''} onChange={(e) => setContact(i, { officePhone: formatPhoneInput(e.target.value, { allowExtension: true }) })} disabled={!inputs} />
              {inputs && (
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  {/* Up / Down arrows let the user reorder contacts.
                      Disabled at the list boundaries so the buttons are
                      visible but inert — keeps the row layout stable
                      regardless of position. */}
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => moveContact(i, -1)}
                    disabled={i === 0}
                    title="Move up"
                    aria-label="Move contact up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => moveContact(i, 1)}
                    disabled={i === (model.plantContacts || []).length - 1}
                    title="Move down"
                    aria-label="Move contact down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => removeContact(i)}
                    title="Remove"
                    aria-label="Remove contact"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          ))}
        </Row>
      </Section>

      <Section title="Sequencing Facilities">
        <Row
          onAdd={inputs ? addFacility : undefined}
          addLabel="+ Add Sequencing Facility"
        >
          {(model.sequencingFacilities || []).length === 0 && (
            <p className="muted tiny">No sequencing facilities yet.</p>
          )}
          {(model.sequencingFacilities || []).map((f, i) => (
            <div
              key={i}
              style={{
                border: '1px solid var(--brand-border)',
                borderRadius: 6,
                padding: 12,
                marginBottom: 10,
                position: 'relative',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    color: 'var(--brand-muted)',
                  }}
                >
                  Facility {i + 1}
                </span>
                {inputs && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => removeFacility(i)}
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="form-grid">
                <TextField
                  label="Name"
                  value={f.name || ''}
                  onChange={(v) => setFacility(i, { name: v })}
                  disabled={!inputs}
                />
                <TextField
                  label="Address"
                  value={f.address || ''}
                  onChange={(v) => setFacility(i, { address: v })}
                  disabled={!inputs}
                  full
                />
                <TextArea
                  label="Notes"
                  value={f.notes || ''}
                  onChange={(v) => setFacility(i, { notes: v })}
                  disabled={!inputs}
                />
              </div>
            </div>
          ))}
        </Row>
      </Section>

      <Section title="Notes">
        <TextArea
          label=""
          value={model.Notes}
          onChange={(v) => set('Notes', v)}
          disabled={!inputs}
        />
      </Section>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginTop: 24,
        }}
      >
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          * Required fields
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-outline" onClick={onCancel}>
            Cancel
          </button>
          {inputs && (
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Plant'}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}

function Row({ title, onAdd, addLabel, children }) {
  return (
    <div>
      {(title || onAdd) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          {title && (
            <span
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--brand-ink)',
              }}
            >
              {title}
            </span>
          )}
          {onAdd && (
            <button type="button" className="btn btn-outline btn-sm" onClick={onAdd}>
              {addLabel || '+ Add'}
            </button>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

function buildModel(initial) {
  // Proxemics team assignments (RegionalManager, LiaisonManager, liaisons)
  // used to live on the plant itself, but now come from the team roster —
  // anyone whose `plantCodes` includes this plant. The plant form no longer
  // edits those fields; PlantDetail derives them from team on the fly.
  const base = {
    Title: '',
    PlantCode: '',
    OEM: '',
    // Plant classification: OEM / Tier 1 / Tier 2 / Tier 3. Empty = unspecified.
    PlantType: '',
    AddressStreet: '',
    AddressStreet2: '',
    AddressStreet3: '',
    City: '',
    State: '',
    PostalCode: '',
    Country: '',
    Notes: '',
    // Legacy single-number field — preserved on the model so existing
    // data still round-trips, but no longer surfaced in the form
    // UI. Replaced by the three day-of-week shift counts below.
    ShiftsPerDay: '',
    // Day-of-week shift counts. Empty string means "not specified";
    // 0–4 are valid integer choices.
    ShiftsWeekdays: '',
    ShiftsSaturdays: '',
    ShiftsSundays: '',
    // Multi-line notes that replaces the per-shift schedule table.
    ShiftNotes: '',
    VehicleScheduleNotes: '',
    // crewShifts is retired — the Plant form no longer renders the
    // per-shift schedule table. Existing data is read-through and
    // preserved on save so legacy plants don't lose their history.
    // Default for new plants is empty.
    crewShifts: [],
    plantContacts: [emptyContact()],
    vehicles: [emptyVehicle()],
    plantClosures: [],
    ppe: { ...EMPTY_PPE },
    // Sequencing Facilities default to empty so the form doesn't auto-show
    // an empty card prompting the user to fill it in — facilities are
    // optional. Jillian clicks "+ Add Sequencing Facility" to add one.
    sequencingFacilities: [],
  };

  if (!initial) return base;

  const merged = { ...base, ...initial };

  // Normalize JSON-backed fields to defaults if missing on initial.
  merged.crewShifts = initial.crewShifts?.length ? initial.crewShifts : base.crewShifts;
  merged.plantContacts = initial.plantContacts?.length
    ? initial.plantContacts
    : [emptyContact()];
  merged.vehicles = initial.vehicles?.length ? initial.vehicles : [emptyVehicle()];
  // Closures default to empty — unlike contacts/vehicles, we don't want to
  // auto-add a blank row prompting users to fill one in.
  merged.plantClosures = Array.isArray(initial.plantClosures)
    ? initial.plantClosures
    : [];
  merged.ppe = { ...EMPTY_PPE, ...(initial.ppe || {}) };
  // Sequencing Facilities: service normalizes to array, but handle the
  // legacy single-object shape too in case initial data comes from somewhere
  // else. An empty/partial legacy object with no real values is treated as
  // "no facilities yet" so we don't auto-create a blank card.
  if (Array.isArray(initial.sequencingFacilities)) {
    merged.sequencingFacilities = initial.sequencingFacilities;
  } else if (
    initial.sequencingFacility &&
    typeof initial.sequencingFacility === 'object'
  ) {
    const legacy = initial.sequencingFacility;
    const hasData = legacy.name || legacy.address || legacy.notes;
    merged.sequencingFacilities = hasData ? [legacy] : [];
  } else {
    merged.sequencingFacilities = [];
  }

  return merged;
}
